import { LockFill } from '@gravity-ui/icons';
import { AgreeToTermsPolicy, type SignIn } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import { SmartInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import useOnSubmit from './use-on-submit';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signInMethods: SignIn['methods'];
};

type FormState = {
  identifier: IdentifierInputValue;
};

const IdentifierSignInForm = ({ className, autoFocus, signInMethods }: Props) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit(signInMethods);
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isPasskeyFlowProcessing } = useContext(WebAuthnContext);

  const enabledSignInMethods = useMemo(
    () => signInMethods.map(({ identifier }) => identifier),
    [signInMethods]
  );

  const prefilledIdentifier = usePrefilledIdentifier({
    enabledIdentifiers: enabledSignInMethods,
  });

  const {
    watch,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: prefilledIdentifier,
    },
  });

  const formRef = useFocusFirstInvalidField(isSubmitting);

  // The terms modal awaits inside the submit, so without this the button spins behind it.
  const [isConfirmingTerms, setIsConfirmingTerms] = useState(false);

  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch('identifier')
  );

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      if (isPasskeyFlowProcessing) {
        return;
      }

      clearErrorMessage();

      void handleSubmit(async ({ identifier: { type, value } }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        // Check if the user has agreed to the terms and privacy policy before signing in when the policy is set to `Manual`
        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual) {
          setIsConfirmingTerms(true);
          const hasAgreed = await termsValidation();
          setIsConfirmingTerms(false);

          if (!hasAgreed) {
            return;
          }
        }

        await onSubmit(type, value);
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
      isPasskeyFlowProcessing,
    ]
  );

  return (
    <form
      ref={formRef}
      className={classNames('flex flex-col', className)}
      onSubmit={onSubmitHandler}
    >
      <div className="flex flex-col gap-4">
        <Controller
          control={control}
          name="identifier"
          rules={{
            validate: ({ type, value }) => {
              if (!type || !value) {
                return getGeneralIdentifierErrorMessage(enabledSignInMethods, 'required');
              }

              const errorMessage = validateIdentifierField(type, value);

              return errorMessage
                ? getGeneralIdentifierErrorMessage(enabledSignInMethods, 'invalid')
                : true;
            },
            onChange: () => {
              if (errors.identifier) {
                clearErrors('identifier');
              }
            },
          }}
          render={({ field, formState: { defaultValues } }) => (
            <SmartInputField
              autoFocus={autoFocus}
              {...field}
              isDanger={!!errors.identifier || !!errorMessage}
              errorMessage={errors.identifier?.message}
              enabledTypes={enabledSignInMethods}
              defaultValue={defaultValues?.identifier?.value}
            />
          )}
        />

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        {showSingleSignOnForm && (
          <p className="text-sm text-muted">{t('description.single_sign_on_enabled')}</p>
        )}

        {/*
         * Hidden with CSS rather than unmounted: unmounting re-renders the form, which re-runs
         * autofill, which pins the SSO form open for an SSO-enabled autofilled value.
         */}
        <TermsAndPrivacyCheckbox
          className={classNames(agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && 'hidden')}
        />

        <CaptchaBox />
      </div>
      <Button
        name="submit"
        className="mt-6"
        title={showSingleSignOnForm ? 'action.single_sign_on' : 'action.sign_in'}
        icon={showSingleSignOnForm ? <LockFill /> : undefined}
        htmlType="submit"
        isLoading={(isSubmitting && !isConfirmingTerms) || isPasskeyFlowProcessing}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default IdentifierSignInForm;
